import { supabase } from '../lib/supabase'

export interface Ticket {
  id: string
  company_id: string
  company_name?: string
  created_by_user_id?: string | null
  assigned_to_user_id?: string | null
  title: string
  description?: string | null
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed'
  device_id?: string | null
  resolved_at?: string | null
  created_at: string
  updated_at: string
  // Additional fields for display
  created_by_email?: string
  assigned_to_email?: string
  device_name?: string
  device_status?: string
  // Detailed fields for issue details page
  steps_taken?: string
  impact?: string
  error_logs?: string
  attachments?: Array<{url: string, filename: string, type: string}>
  resolution_notes?: string
}

export interface CreateTicketData {
  title: string
  description?: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  device_id?: string
  assigned_to_user_id?: string
}

export const ticketsService = {
  // Get all tickets for a company (or all companies for admins)
  async getTickets(companyId?: string): Promise<Ticket[]> {
    let query = supabase
      .from('tickets')
      .select(`
        *,
        company:companies(name),
        created_by:users!tickets_created_by_user_id_fkey(email),
        assigned_to:users!tickets_assigned_to_user_id_fkey(email),
        device:devices(name, status)
      `)
      .order('created_at', { ascending: false })

    if (companyId) {
      query = query.eq('company_id', companyId)
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching tickets:', error)
      throw error
    }

    // Transform the data to include the related fields
    return data.map(ticket => ({
      ...ticket,
      company_name: ticket.company?.name,
      created_by_email: ticket.created_by?.email,
      assigned_to_email: ticket.assigned_to?.email,
      device_name: ticket.device?.name,
      device_status: ticket.device?.status
    }))
  },

  // Get a single ticket by ID
  async getTicket(ticketId: string): Promise<Ticket> {
    const { data, error } = await supabase
      .from('tickets')
      .select(`
        *,
        created_by:users!tickets_created_by_user_id_fkey(email),
        assigned_to:users!tickets_assigned_to_user_id_fkey(email),
        device:devices(name, status)
      `)
      .eq('id', ticketId)
      .single()

    if (error) {
      console.error('Error fetching ticket:', error)
      throw error
    }

    return {
      ...data,
      company_name: data.company?.name,
      created_by_email: data.created_by?.email,
      assigned_to_email: data.assigned_to?.email,
      device_name: data.device?.name,
      device_status: data.device?.status
    }
  },

  // Create a new ticket
  async createTicket(ticketData: CreateTicketData, companyId: string, createdByUserId: string): Promise<Ticket> {
    const { data, error } = await supabase
      .from('tickets')
      .insert({
        company_id: companyId,
        created_by_user_id: createdByUserId,
        title: ticketData.title,
        description: ticketData.description,
        priority: ticketData.priority || 'medium',
        device_id: ticketData.device_id,
        assigned_to_user_id: ticketData.assigned_to_user_id,
        status: 'open'
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating ticket:', error)
      throw error
    }

    return data
  },

  // Update a ticket
  async updateTicket(ticketId: string, updates: Partial<Ticket>): Promise<Ticket> {
    const { data, error } = await supabase
      .from('tickets')
      .update(updates)
      .eq('id', ticketId)
      .select()
      .single()

    if (error) {
      console.error('Error updating ticket:', error)
      throw error
    }

    return data
  },

  // Delete a ticket
  async deleteTicket(ticketId: string): Promise<void> {
    const { error } = await supabase
      .from('tickets')
      .delete()
      .eq('id', ticketId)

    if (error) {
      console.error('Error deleting ticket:', error)
      throw error
    }
  }
}
